<script>
	import { debounce } from "lodash";
	import queryString from "query-string";
	import ButtonWrapper from "src/components/ButtonWrapper/ButtonWrapper.svelte";
	import { convertDateOnly } from "src/services/helpers";
	import {
		exportServicingCsv,
		getMissingServicingRules,
		getServicingList,
	} from "src/services/fmsTasking";
	import { extractErrorMessage } from "src/utils/misc";
	import { onMount, onDestroy } from "svelte";
	import { field, form } from "svelte-forms";
	import { navigate } from "svelte-routing";
	import { Col, Row } from "@sveltestrap/sveltestrap";
	import CustomButton from "../../../components/CustomButton/CustomButton.svelte";
	import CustomInput from "../../../components/CustomInput/CustomInput.svelte";
	import ErrorBanner from "../../../components/ErrorBanner/ErrorBanner.svelte";
	import ErrorMessageHandler from "../../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import PageHeader from "../../../components/PageHeader/PageHeader.svelte";
	import LimitSize from "../../../components/SizeSelect/limitSize.svelte";
	import Tabs from "../../../components/Tabs/Tabs.svelte";
	import {
		errorMessage,
		initPage,
		loading,
		sizeSelect,
		totalPages,
	} from "../../../store/store";
	import ServiceTable from "./ServiceTable.svelte";

	let err;
	let pageSize = "20";
	$: query = queryString.parse(window.location.search);
	let data = {
		totalCount: 0,
		totalPage: 1,
		pageNumber: 1,
		pageSize: 20,
		data: [],
		statusCounts: {
			all: 0,
			dueSoon: 0,
			overdue: 0,
		},
	};
	let missingRules = false;
	$: activeTab = parseInt(query.status) || 0;
	const searchCarPlate = field("searchCarPlate", "");
	const myForm = form(searchCarPlate);

	let params = {
		page: 1,
		size: 20,
		status: 0,
	};
	let sort = {
		sortCol: "",
		sortDir: "",
	};

	async function fetchServiceData(plateNumber, sort) {
		if (!plateNumber && query.plateNumber) {
			plateNumber = query.plateNumber;
		}
		if (plateNumber) {
			params = {
				...params,
				plateNumber: plateNumber,
			};
		} else {
			delete params.plateNumber;
		}
		if (sort?.sortCol) {
			params = {
				...params,
				...sort,
			};
		}
		try {
			loading.set(true);
			const res = await getServicingList(params);
			if (res.status === 200) {
				data = res.data;
				totalPages.set(data.totalPage);
				loading.set(false);
			}
		} catch (error) {
			extractErrorMessage(error);
			console.log(error);
			loading.set(false);
		}
	}

	async function fetchMissingRules() {
		try {
			const res = await getMissingServicingRules();
			if (res.status === 200) {
				missingRules = Boolean(res.data.length);
			}
		} catch (error) {
			extractErrorMessage("Failed to fetch missing rules");
			console.log(error);
		}
	}

	function handleSearchDebounce(e) {
		if (!e) {
			delete params.plateNumber;
			delete query.plateNumber;
			navigate(`/servicing?page=1`);
		} else {
			query.page = 1;
			params.page = 1;
			initPage.set(1);
			navigate(`/servicing?page=1&plateNumber=${e}`);
		}
		fetchServiceData(e);
	}

	function handleSort(sortCol, sortDir) {
		sort = {
			sortCol,
			sortDir,
		};
		fetchServiceData(searchCarPlate.value, sort);
	}

	const debouncedInput = debounce((e) => handleSearchDebounce(e), 1000);

	onMount(() => {
		if (!query.page) {
			query.page = 1;
			initPage.set(1);
			navigate(`/servicing?page=1&status=1`);
		} else {
			params.page = parseInt(query.page);
			initPage.set(parseInt(query.page));
		}

		if (!query.status) {
			query.status = 1;
		} else {
			params.status = activeTab - 1;
		}

		if (query.plateNumber) {
			$searchCarPlate.value = query.plateNumber;
		}
		fetchServiceData();
		fetchMissingRules();
	});

	function changeTab(index) {
		activeTab = index;
		params.status = index;
		params.page = 1;
		initPage.set(1);
		navigate(`/servicing?page=1&status=${index + 1}`);
		fetchServiceData();
	}

	errorMessage.subscribe((value) => {
		err = value;
	});

	const unsubscribeSizeSelect = sizeSelect.subscribe((value) => {
		if (pageSize !== value) {
			pageSize = value;
			params.size = value;
			fetchServiceData();
		}
	});

	onDestroy(() => {
		unsubscribeSizeSelect();
	});

	$: items = [
		{
			label: `All (${data.statusCounts.all})`,
			value: 1,
		},
		{
			label: `Overdue (${data.statusCounts.overdue})`,
			value: 3,
		},
		{
			label: `Due soon (${data.statusCounts.dueSoon})`,
			value: 2,
		},
	];
</script>

<Row>
	<Col xs={12}>
		<div>
			<ErrorMessageHandler message={err} />
			<Row>
				<Col className="row d-flex align-items-start mx-0">
					<PageHeader title="Servicing" />
				</Col>
				<Col class="d-flex flex-row-reverse">
					<div class="ms-2">
						<CustomButton
							buttonText="Export"
							icon="/assets/icon-download-thin.svg"
							on:click={exportServicingCsv}
							onKeyDown={() => {}}
						/>
					</div>
					<div class="me-2">
						<CustomButton
							buttonText="Manage rules"
							icon="/assets/icon-gear-thin.svg"
							on:click={() =>
								navigate("/servicing/manage-rules?page=1")}
							on:keydown={() => {}}
						/>
					</div>
				</Col>
			</Row>
			<Row>
				<Col>
					<Tabs {items} fetchServiceData={changeTab} {activeTab} />
				</Col>
			</Row>
			{#if missingRules}
				<ErrorBanner
					errorHeader="Warning"
					errorText="You have pending actions that require your immediate attention."
				>
					<ButtonWrapper
						on:keydown={() => {}}
						on:click={() =>
							navigate("/servicing/manage-rules?page=1")}
					>
						<span class="link link-text"> Manage rules</span>
					</ButtonWrapper>
				</ErrorBanner>
			{/if}
			<Row class="d-flex mb-3">
				<Col xs={3} class="d-flex align-items-end">
					<CustomInput
						label="Search"
						type="text"
						placeholder="Search carplate no."
						bind:value={$searchCarPlate.value}
						search
						onInput={(e) => debouncedInput(e.target.value)}
					/>
				</Col>
				<Col xs={5} />
				<Col xs={4}>
					<Row>
						<Col class="d-flex justify-content-end">
							<span class="on-surface-variant"
								>Last updated {convertDateOnly(
									data.updatedDetails?.updatedAt,
									"MMM D, h:mm A"
								) || ""}</span
							>
						</Col>
					</Row>
					<Row>
						<Col>
							<LimitSize trigerSelect={fetchServiceData} />
						</Col>
					</Row>
				</Col>
			</Row>
		</div>
	</Col>
	<Col>
		<ServiceTable {data} {handleSort} />
	</Col>
</Row>
